import { supabase } from '../../lib/supabase';
import { getUserByAuthId } from './usersService';
import { getGameNameById } from '../../constants/deckRules';

export interface TournamentMetaDeck {
  id: number | string;
  game_id: number | string;
  name: string;
  slug?: string;
  colors?: string[] | null;
  image_url?: string | null;
  active?: boolean;
  code?: string | null;
  display_name?: string | null;
  representative_card_id?: string | null;
  game_format_id?: string | number | null;
  created_at?: string;
  updated_at?: string;
  rounds_count?: number;
  reports_count?: number;
}

export interface UserTournamentReportRound {
  id?: string | number;
  report_id?: string | number;
  round_number: number;
  opponent_name?: string | null;
  opponent_deck?: string | null;
  opponent_meta_deck_id?: number | string | null;
  opponent_meta_deck?: TournamentMetaDeck | null;
  opponent_deck_image_url?: string | null;
  won_dice?: boolean | null;
  play_order?: 1 | 2 | null;
  result: 'win' | 'loss' | 'draw';
  created_at?: string;
}

export type ValidationStatus = 'self_reported' | 'pending' | 'validated' | 'rejected';

export interface UserTournamentReport {
  id: string | number;
  user_id: string | number;
  user_name?: string;
  user_avatar?: string;
  game_id: number | string;
  game_name?: string;
  name: string; // text NOT NULL
  played_at: string; // date NOT NULL

  deck_id?: string | null; // uuid NULL
  deck_name?: string | null; // text NULL
  deck_data?: any; // jsonb NULL

  format_id?: number | string | null; // bigint NULL
  format_name?: string | null;
  tournament_type_id?: number | string | null; // bigint NULL
  tournament_type_name?: string | null;
  event_category_id?: number | string | null; // bigint NULL
  event_category_name?: string | null;
  tourney_set_id?: number | string | null; // bigint NULL
  tourney_set_name?: string | null;

  placement?: number | string | null; // integer NULL (ex: 1, 2, 4, 8)
  total_players?: number | null; // integer NULL

  wins: number; // integer NOT NULL default 0
  losses: number; // integer NOT NULL default 0
  draws: number; // integer NOT NULL default 0

  official_tournament_id?: number | string | null; // bigint NULL
  validation_status: ValidationStatus; // text NOT NULL default 'self_reported'

  created_at: string;
  updated_at?: string;

  // Campos amigáveis para UI
  deck_leader?: string | null;
  deck_leader_image?: string | null;
  store_name?: string | null;
  location?: string | null;
  notes?: string | null;
  rounds?: UserTournamentReportRound[];
}

export const EVENT_CATEGORIES = [
  { id: '1', name: 'Local / Semanal', slug: 'local', description: 'Torneio regular da loja local' },
  { id: '2', name: 'Store Championship', slug: 'store_championship', description: 'Campeonato de loja / Store Showdown' },
  { id: '3', name: 'Treasure Cup / Set Championship', slug: 'treasure_cup', description: 'Torneio especial de lançamento / Cup' },
  { id: '4', name: 'Flagship Battle', slug: 'flagship', description: 'Competição com premiação especial' },
  { id: '5', name: 'Regional Championship', slug: 'regional', description: 'Torneio Regional oficial' },
  { id: '6', name: 'National Championship', slug: 'national', description: 'Campeonato Nacional' },
  { id: '7', name: 'World Championship', slug: 'world', description: 'Mundial oficial' }
];

export const getTournamentEventCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('tournament_event_categories')
      .select('*')
      .order('id', { ascending: true });
    if (!error && Array.isArray(data) && data.length > 0) {
      return data;
    }
  } catch (err) {
    console.warn('Could not fetch tournament_event_categories:', err);
  }
  return EVENT_CATEGORIES;
};

export const TOURNAMENT_FORMATS = [
  { id: '1', name: 'Constructed (Padrão/Premier)', slug: 'constructed', description: 'Deck construído previamente' },
  { id: '2', name: 'Standard', slug: 'standard', description: 'Formato padrão rotativo' },
  { id: '3', name: 'Premier', slug: 'premier', description: 'Formato competitivo principal SWU' },
  { id: '4', name: 'Sealed (Selado)', slug: 'sealed', description: 'Construção no local com boosters' },
  { id: '5', name: 'Draft / Booster Draft', slug: 'draft', description: 'Escolha alternada de cartas' },
  { id: '6', name: 'Commander / EDH', slug: 'commander', description: 'Formato singleton 100 cartas' }
];

export const DEFAULT_META_DECKS: TournamentMetaDeck[] = [
  // One Piece (game_id: 1)
  { id: 101, game_id: 1, name: 'Enel', display_name: 'Enel (Yellow Control)', slug: 'enel', colors: ['Yellow'], image_url: 'https://images.limitlesstcg.com/one-piece/OP05/OP05-098.webp' },
  { id: 102, game_id: 1, name: 'Trafalgar Law', display_name: 'RP Law (Red/Purple Law)', slug: 'rp-law', colors: ['Red', 'Purple'], image_url: 'https://images.limitlesstcg.com/one-piece/EB01/EB01-061.webp' },
  { id: 103, game_id: 1, name: 'Rob Lucci', display_name: 'Rob Lucci (Black CP9)', slug: 'rob-lucci', colors: ['Black'], image_url: 'https://images.limitlesstcg.com/one-piece/OP07/OP07-079.webp' },
  { id: 104, game_id: 1, name: 'Jewelry Bonney', display_name: 'Jewelry Bonney (Green Supernovas)', slug: 'jewelry-bonney', colors: ['Green'], image_url: 'https://images.limitlesstcg.com/one-piece/OP07/OP07-019.webp' },
  { id: 105, game_id: 1, name: 'Donquixote Doflamingo', display_name: 'Doflamingo (Blue Warlords)', slug: 'doflamingo', colors: ['Blue'], image_url: 'https://images.limitlesstcg.com/one-piece/OP07/OP07-040.webp' },
  { id: 106, game_id: 1, name: 'Roronoa Zoro', display_name: 'Zoro (Red Aggro)', slug: 'zoro', colors: ['Red'], image_url: 'https://images.limitlesstcg.com/one-piece/OP01/OP01-001.webp' },
  { id: 107, game_id: 1, name: 'Shanks', display_name: 'Shanks (Red Red Hair Pirates)', slug: 'shanks', colors: ['Red'], image_url: 'https://images.limitlesstcg.com/one-piece/OP09/OP09-001.webp' },
  { id: 108, game_id: 1, name: 'Gecko Moria', display_name: 'Gecko Moria (Black Thriller Bark)', slug: 'gecko-moria', colors: ['Black'], image_url: 'https://images.limitlesstcg.com/one-piece/OP06/OP06-080.webp' },
  { id: 109, game_id: 1, name: 'Nami', display_name: 'Nami (Blue Mill/Alternate Win)', slug: 'nami', colors: ['Blue'], image_url: 'https://images.limitlesstcg.com/one-piece/OP03/OP03-040.webp' },
  { id: 110, game_id: 1, name: 'Charlotte Katakuri', display_name: 'Katakuri (Yellow Big Mom Pirates)', slug: 'katakuri', colors: ['Yellow'], image_url: 'https://images.limitlesstcg.com/one-piece/OP03/OP03-099.webp' },
  { id: 111, game_id: 1, name: 'Monkey D. Luffy', display_name: 'Black Luffy / Gear 5', slug: 'luffy-black', colors: ['Black'], image_url: 'https://images.limitlesstcg.com/one-piece/ST14/ST14-001.webp' },
  { id: 112, game_id: 1, name: 'Boa Hancock', display_name: 'Boa Hancock (Blue Seven Warlords)', slug: 'boa-hancock', colors: ['Blue'], image_url: 'https://images.limitlesstcg.com/one-piece/OP07/OP07-038.webp' },

  // Star Wars Unlimited (game_id: 10)
  { id: 1001, game_id: 10, name: 'Sabine Wren', display_name: 'Sabine Wren (Aggro Yellow/ECL)', slug: 'sabine-wren', colors: ['Aggression', 'Cunning'], image_url: 'https://images.swunlimited.com/cards/sor/014.png' },
  { id: 1002, game_id: 10, name: 'Boba Fett', display_name: 'Boba Fett (Green/Yellow Midrange)', slug: 'boba-fett', colors: ['Cunning', 'Command'], image_url: 'https://images.swunlimited.com/cards/sor/015.png' },
  { id: 1003, game_id: 10, name: 'Darth Vader', display_name: 'Darth Vader (Command/Villainy Control)', slug: 'darth-vader', colors: ['Command', 'Villainy'], image_url: 'https://images.swunlimited.com/cards/sor/010.png' },
  { id: 1004, game_id: 10, name: 'Han Solo', display_name: 'Han Solo (Yellow/Green Tempo)', slug: 'han-solo', colors: ['Cunning', 'Heroism'], image_url: 'https://images.swunlimited.com/cards/sor/017.png' },
  { id: 1005, game_id: 10, name: 'Grand Moff Tarkin', display_name: 'Grand Moff Tarkin (Space Swarm)', slug: 'tarkin', colors: ['Command', 'Villainy'], image_url: 'https://images.swunlimited.com/cards/sor/008.png' },
  { id: 1006, game_id: 10, name: 'Luke Skywalker', display_name: 'Luke Skywalker (Heroism Shield Midrange)', slug: 'luke-skywalker', colors: ['Vigilance', 'Heroism'], image_url: 'https://images.swunlimited.com/cards/sor/005.png' },
  { id: 1007, game_id: 10, name: 'Emperor Palpatine', display_name: 'Emperor Palpatine (Blue Hard Control)', slug: 'palpatine', colors: ['Vigilance', 'Villainy'], image_url: 'https://images.swunlimited.com/cards/sor/006.png' },
  { id: 1008, game_id: 10, name: 'Rey', display_name: 'Rey (Heroism / Force Midrange)', slug: 'rey', colors: ['Heroism', 'Vigilance'], image_url: 'https://images.swunlimited.com/cards/shd/001.png' },
  { id: 1009, game_id: 10, name: 'Kylo Ren', display_name: 'Kylo Ren (Aggro Villainy)', slug: 'kylo-ren', colors: ['Aggression', 'Villainy'], image_url: 'https://images.swunlimited.com/cards/shd/011.png' },

  // Pokémon (game_id: 3)
  { id: 301, game_id: 3, name: 'Charizard ex', display_name: 'Charizard ex (Dark Tera)', slug: 'charizard-ex', colors: ['Darkness', 'Fire'] },
  { id: 302, game_id: 3, name: 'Gardevoir ex', display_name: 'Gardevoir ex (Psychic Engine)', slug: 'gardevoir-ex', colors: ['Psychic'] },
  { id: 303, game_id: 3, name: 'Miraidon ex', display_name: 'Miraidon ex (Electric Turbo)', slug: 'miraidon-ex', colors: ['Lightning'] },
  { id: 304, game_id: 3, name: 'Raging Bolt ex', display_name: 'Raging Bolt ex / Ogerpon', slug: 'raging-bolt-ex', colors: ['Dragon', 'Grass'] },
  { id: 305, game_id: 3, name: 'Dragapult ex', display_name: 'Dragapult ex (Phantom Dive)', slug: 'dragapult-ex', colors: ['Dragon'] },
  { id: 306, game_id: 3, name: 'Lugia VSTAR', display_name: 'Lugia VSTAR / Archeops', slug: 'lugia-vstar', colors: ['Colorless'] },
  { id: 307, game_id: 3, name: 'Roaring Moon ex', display_name: 'Roaring Moon ex (Turbo Dark)', slug: 'roaring-moon-ex', colors: ['Darkness'] },
  { id: 308, game_id: 3, name: 'Iron Thorns ex', display_name: 'Iron Thorns ex (Quad Thorns)', slug: 'iron-thorns-ex', colors: ['Lightning'] },

  // Digimon (game_id: 2)
  { id: 201, game_id: 2, name: 'Imperialdramon', display_name: 'Imperialdramon (Blue/Green DNA)', slug: 'imperialdramon', colors: ['Blue', 'Green'] },
  { id: 202, game_id: 2, name: 'MagnaX Vaccine', display_name: 'Magnamon X (Armor Vaccine)', slug: 'magnax', colors: ['Yellow', 'Blue'] },
  { id: 203, game_id: 2, name: 'Demon Lords', display_name: 'Seven Great Demon Lords (Gate)', slug: 'demon-lords', colors: ['Purple'] },
  { id: 204, game_id: 2, name: 'Takemikazuchi', display_name: 'Fenriloogamon: Takemikazuchi', slug: 'takemikazuchi', colors: ['Purple'] },
  { id: 205, game_id: 2, name: 'MirageGaogamon', display_name: 'MirageGaogamon Burst Mode', slug: 'miragegaogamon', colors: ['Blue'] },
  { id: 206, game_id: 2, name: 'Numemon', display_name: 'Numemon / Monzaemon Rush', slug: 'numemon', colors: ['Yellow', 'Black'] },

  // Magic (game_id: 4)
  { id: 401, game_id: 4, name: 'Mono-Red Aggro', display_name: 'Mono-Red Aggro / Prowess', slug: 'mono-red', colors: ['Red'] },
  { id: 402, game_id: 4, name: 'Dimir Midrange', display_name: 'Dimir Midrange (UB)', slug: 'dimir-midrange', colors: ['Blue', 'Black'] },
  { id: 403, game_id: 4, name: 'Golgari Midrange', display_name: 'Golgari Midrange (GB)', slug: 'golgari-midrange', colors: ['Black', 'Green'] },
  { id: 404, game_id: 4, name: 'Domain Ramp', display_name: 'Domain Ramp / Atraxa (5C)', slug: 'domain-ramp', colors: ['White', 'Blue', 'Black', 'Red', 'Green'] },
  { id: 405, game_id: 4, name: 'Boros Convoke', display_name: 'Boros Convoke (WR Aggro)', slug: 'boros-convoke', colors: ['White', 'Red'] },
  { id: 406, game_id: 4, name: 'Azorius Control', display_name: 'Azorius Control (UW)', slug: 'azorius-control', colors: ['White', 'Blue'] },

  // Yu-Gi-Oh! (game_id: 5)
  { id: 501, game_id: 5, name: 'Snake-Eye', display_name: 'Snake-Eye / Fiendsmith', slug: 'snake-eye', colors: ['FIRE'] },
  { id: 502, game_id: 5, name: 'Tenpai Dragon', display_name: 'Tenpai Dragon (Synchro OTK)', slug: 'tenpai-dragon', colors: ['FIRE'] },
  { id: 503, game_id: 5, name: 'Yubel', display_name: 'Fiendsmith Yubel', slug: 'yubel', colors: ['DARK'] },
  { id: 504, game_id: 5, name: 'Voiceless Voice', display_name: 'Voiceless Voice (Ritual)', slug: 'voiceless-voice', colors: ['LIGHT'] },
  { id: 505, game_id: 5, name: 'Centur-Ion', display_name: 'Centur-Ion (Synchro)', slug: 'centur-ion', colors: ['DARK', 'LIGHT'] },

  // Lorcana (game_id: 8)
  { id: 801, game_id: 8, name: 'Ruby/Amethyst', display_name: 'Ruby/Amethyst Bounce Control', slug: 'ruby-amethyst', colors: ['Ruby', 'Amethyst'] },
  { id: 802, game_id: 8, name: 'Emerald/Steel', display_name: 'Emerald/Steel Tempo Discard', slug: 'emerald-steel', colors: ['Emerald', 'Steel'] },
  { id: 803, game_id: 8, name: 'Amber/Steel', display_name: 'Amber/Steel Steelsongs', slug: 'amber-steel', colors: ['Amber', 'Steel'] },
  { id: 804, game_id: 8, name: 'Sapphire/Steel', display_name: 'Sapphire/Steel Items Ramp', slug: 'sapphire-steel', colors: ['Sapphire', 'Steel'] },
  { id: 805, game_id: 8, name: 'Ruby/Sapphire', display_name: 'Ruby/Sapphire Sisu Ramp', slug: 'ruby-sapphire', colors: ['Ruby', 'Sapphire'] }
];

export const getTournamentMetaDecks = async (
  gameId?: number | string,
  formatId?: number | string
): Promise<TournamentMetaDeck[]> => {
  const numericGameId = gameId && gameId !== 'All' ? Number(gameId) : null;
  const numericFormatId = formatId && formatId !== 'All' ? Number(formatId) : null;

  // 1. Consultar a view public.v_tournament_meta_decks_stats
  try {
    let viewQuery = supabase
      .from('v_tournament_meta_decks_stats')
      .select('*');

    if (numericGameId) {
      viewQuery = viewQuery.eq('game_id', numericGameId);
    }
    if (numericFormatId) {
      viewQuery = viewQuery.eq('game_format_id', numericFormatId);
    }

    // Ordenar por volume de rodadas e reports, seguido de nome
    viewQuery = viewQuery
      .order('rounds_count', { ascending: false })
      .order('reports_count', { ascending: false })
      .order('name', { ascending: true });

    const { data: viewData, error: viewError } = await viewQuery;
    if (!viewError && Array.isArray(viewData) && viewData.length > 0) {
      // Agrupar e desduplicar por ID único para evitar múltiplos registros do mesmo meta deck (ex: formatos diferentes na view)
      const deckMap = new Map<string, TournamentMetaDeck>();
      for (const d of viewData) {
        if (!d || d.id === undefined || d.id === null) continue;
        const idKey = String(d.id);
        const rounds = Number(d.rounds_count) || 0;
        const reports = Number(d.reports_count) || 0;

        if (deckMap.has(idKey)) {
          const existing = deckMap.get(idKey)!;
          existing.rounds_count = (existing.rounds_count || 0) + rounds;
          existing.reports_count = (existing.reports_count || 0) + reports;
          if (!existing.image_url && d.image_url) existing.image_url = d.image_url;
          if (!existing.code && d.code) existing.code = d.code;
          if (!existing.display_name && d.display_name) existing.display_name = d.display_name;
        } else {
          deckMap.set(idKey, {
            ...d,
            rounds_count: rounds,
            reports_count: reports
          });
        }
      }

      const deduplicatedList = Array.from(deckMap.values());
      deduplicatedList.sort((a, b) => {
        const diffRounds = (b.rounds_count || 0) - (a.rounds_count || 0);
        if (diffRounds !== 0) return diffRounds;
        const diffReports = (b.reports_count || 0) - (a.reports_count || 0);
        if (diffReports !== 0) return diffReports;
        return (a.name || '').localeCompare(b.name || '');
      });

      return deduplicatedList;
    } else if (viewError) {
      console.warn('Could not fetch from v_tournament_meta_decks_stats view:', viewError.message);
    }
  } catch (err) {
    console.warn('Exception querying v_tournament_meta_decks_stats view:', err);
  }

  // 2. Fallback para tournament_meta_decks
  try {
    let query = supabase
      .from('tournament_meta_decks')
      .select('*')
      .order('name', { ascending: true });

    if (numericGameId) {
      query = query.eq('game_id', numericGameId);
    }

    const { data, error } = await query;
    if (!error && Array.isArray(data) && data.length > 0) {
      const deckMap = new Map<string, TournamentMetaDeck>();
      data.forEach(d => {
        if (d && d.id !== undefined && d.id !== null) {
          deckMap.set(String(d.id), d);
        }
      });
      return Array.from(deckMap.values());
    }
  } catch (err) {
    console.warn('Could not fetch tournament_meta_decks from Supabase:', err);
  }

  // 3. Fallback para DEFAULT_META_DECKS
  if (numericGameId) {
    const list = DEFAULT_META_DECKS.filter(d => Number(d.game_id) === numericGameId);
    const deckMap = new Map<string, TournamentMetaDeck>();
    list.forEach(d => deckMap.set(String(d.id), d));
    return Array.from(deckMap.values());
  }
  const deckMap = new Map<string, TournamentMetaDeck>();
  DEFAULT_META_DECKS.forEach(d => deckMap.set(String(d.id), d));
  return Array.from(deckMap.values());
};

export const getTournamentFormatsForReports = async (gameId?: number | string) => {
  try {
    let query = supabase
      .from('game_formats')
      .select('*')
      .order('name', { ascending: true });

    if (gameId && gameId !== 'All') {
      const numGameId = Number(gameId);
      if (!isNaN(numGameId)) {
        query = query.or(`game_id.eq.${numGameId},game_id.is.null`);
      }
    }

    const { data, error } = await query;

    if (!error && Array.isArray(data) && data.length > 0) {
      return data;
    }

    // Fallback: if game_formats returned error or empty, try legacy table
    if (error) {
      console.warn('Could not fetch game_formats, trying tournament_formats fallback:', error);
      const { data: legacyData } = await supabase
        .from('tournament_formats')
        .select('*')
        .order('name', { ascending: true });
      if (Array.isArray(legacyData) && legacyData.length > 0) {
        if (gameId && gameId !== 'All') {
          const numGameId = Number(gameId);
          const filtered = legacyData.filter((f: any) =>
            (f.game_id !== undefined && f.game_id !== null && Number(f.game_id) === numGameId) ||
            (!f.game_id && !f.cardgame_id)
          );
          if (filtered.length > 0) return filtered;
        }
        return legacyData;
      }
    }
  } catch (err) {
    console.warn('Could not fetch game_formats/tournament_formats:', err);
  }
  return TOURNAMENT_FORMATS;
};

export const TOURNAMENT_TYPES = [
  { id: '1', name: 'Swiss + Top Cut', slug: 'swiss_topcut', description: 'Rodadas suíças seguidas de mata-mata' },
  { id: '2', name: 'Swiss (Suíço Puro)', slug: 'swiss', description: 'Apenas rodadas suíças sem corte' },
  { id: '3', name: 'Single Elimination (Mata-mata)', slug: 'single_elimination', description: 'Eliminação direta em chave simples' },
  { id: '4', name: 'Round Robin', slug: 'round_robin', description: 'Todos contra todos' }
];

export const getTournamentTypes = async () => {
  try {
    const { data, error } = await supabase
      .from('tournament_types')
      .select('*')
      .order('id', { ascending: true });
    if (!error && Array.isArray(data) && data.length > 0) {
      return data;
    }
  } catch (err) {
    console.warn('Could not fetch tournament_types:', err);
  }
  return TOURNAMENT_TYPES;
};

export interface GetGameSetsOptions {
  tourneyRestrictOnly?: boolean;
}

export const getGameSetsForReports = async (
  gameId?: number | string,
  options?: GetGameSetsOptions
) => {
  const onlyRestrict = options?.tourneyRestrictOnly ?? false;

  // Se não foi passado gameId ou é 'All'
  if (!gameId || gameId === 'All') {
    if (onlyRestrict) {
      try {
        const { data, error } = await supabase
          .from('game_sets')
          .select('*')
          .eq('tourney_restrict', true)
          .order('release_date', { ascending: false });

        if (!error && Array.isArray(data) && data.length > 0) {
          return data.filter((s: any) => s.tourney_restrict === true || s.tourney_restrict === 'true' || s.tourney_restrict === 1);
        }
      } catch (err) {
        console.warn('Could not fetch tourney_restrict game_sets for All:', err);
      }
    }
    return [];
  }

  const numericGameId = Number(gameId);

  // 1. Tentar buscar direto do Supabase filtrando por game_id (e opcionalmente tourney_restrict)
  try {
    let query = supabase
      .from('game_sets')
      .select('*')
      .eq('game_id', numericGameId);

    if (onlyRestrict) {
      query = query.eq('tourney_restrict', true);
    }

    const { data, error } = await query.order('release_date', { ascending: false });

    if (!error && Array.isArray(data) && data.length > 0) {
      return onlyRestrict
        ? data.filter((s: any) => s.tourney_restrict === true || s.tourney_restrict === 'true' || s.tourney_restrict === 1)
        : data;
    }
  } catch (err) {
    console.warn('Could not fetch game_sets by game_id:', err);
  }

  // 2. Tentar buscar por cardgame_id se a coluna for diferente
  try {
    let query = supabase
      .from('game_sets')
      .select('*')
      .eq('cardgame_id', numericGameId);

    if (onlyRestrict) {
      query = query.eq('tourney_restrict', true);
    }

    const { data, error } = await query.order('release_date', { ascending: false });

    if (!error && Array.isArray(data) && data.length > 0) {
      return onlyRestrict
        ? data.filter((s: any) => s.tourney_restrict === true || s.tourney_restrict === 'true' || s.tourney_restrict === 1)
        : data;
    }
  } catch (err) {
    console.warn('Could not fetch game_sets by cardgame_id:', err);
  }

  // 3. Fallback: buscar lista geral e filtrar estritamente pelo jogo selecionado em memória
  try {
    let query = supabase
      .from('game_sets')
      .select('*');

    if (onlyRestrict) {
      query = query.eq('tourney_restrict', true);
    }

    const { data, error } = await query.order('name', { ascending: true });

    if (!error && Array.isArray(data)) {
      return data.filter((s: any) => {
        const matchesGame =
          (s.game_id !== undefined && s.game_id !== null && Number(s.game_id) === numericGameId) ||
          (s.cardgame_id !== undefined && s.cardgame_id !== null && Number(s.cardgame_id) === numericGameId);
        const matchesRestrict = onlyRestrict
          ? (s.tourney_restrict === true || s.tourney_restrict === 'true' || s.tourney_restrict === 1)
          : true;
        return matchesGame && matchesRestrict;
      });
    }
  } catch (err) {
    console.warn('Could not fetch game_sets fallback:', err);
  }

  return [];
};

const LOCAL_STORAGE_KEY = 'cardumy_user_tournament_reports_v4';

export const getStoredReports = (): UserTournamentReport[] => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (err) {
    console.error('Error reading reports from localStorage:', err);
  }
  return [];
};

export const saveStoredReports = (reports: UserTournamentReport[]) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(reports));
  } catch (err) {
    console.error('Error saving reports to localStorage:', err);
  }
};

/**
 * Normaliza o registro vindo do banco de dados (public.user_tournament_reports)
 */
export const mapDbReportToUi = (item: any): UserTournamentReport => {
  const deckData = item.deck_data || item.user_decks?.data || item.deck?.deck_data || null;

  // Processar rodadas do relacionamento do Supabase ou do snapshot
  let rounds: UserTournamentReportRound[] = [];
  if (Array.isArray(item.user_tournament_report_rounds) && item.user_tournament_report_rounds.length > 0) {
    rounds = item.user_tournament_report_rounds
      .map((r: any) => {
        const metaDeck = Array.isArray(r.tournament_meta_decks)
          ? r.tournament_meta_decks[0]
          : (r.tournament_meta_decks || r.opponent_meta_deck || null);

        // Fallback para metadados locais se o join não trouxe o objeto
        const fallbackMetaById = !metaDeck && r.opponent_meta_deck_id
          ? DEFAULT_META_DECKS.find(d => String(d.id) === String(r.opponent_meta_deck_id))
          : null;
        
        const rawDeckName = (r.opponent_deck || r.opponent_deck_name || '').toLowerCase().trim();
        const fallbackMetaByName = (!metaDeck && !fallbackMetaById && rawDeckName)
          ? DEFAULT_META_DECKS.find(d => 
              d.name.toLowerCase().trim() === rawDeckName ||
              (d.display_name && d.display_name.toLowerCase().trim() === rawDeckName) ||
              (d.slug && d.slug.toLowerCase().trim() === rawDeckName) ||
              rawDeckName.includes(d.name.toLowerCase().trim())
            )
          : null;

        const finalMeta = metaDeck || fallbackMetaById || fallbackMetaByName;

        const resolvedOpponentDeck = r.opponent_deck || finalMeta?.display_name || finalMeta?.name || r.opponent_deck_name || null;
        const resolvedOpponentImage = r.opponent_deck_image_url || finalMeta?.image_url || null;

        return {
          id: r.id,
          report_id: r.report_id,
          round_number: Number(r.round_number || 1),
          opponent_name: r.opponent_name || null,
          opponent_deck: resolvedOpponentDeck,
          opponent_meta_deck_id: r.opponent_meta_deck_id ? Number(r.opponent_meta_deck_id) : (finalMeta?.id ? Number(finalMeta.id) : null),
          opponent_meta_deck: finalMeta || null,
          opponent_deck_image_url: resolvedOpponentImage,
          won_dice: r.won_dice !== undefined && r.won_dice !== null ? Boolean(r.won_dice) : null,
          play_order: r.play_order === 1 || r.play_order === 2 ? r.play_order : null,
          result: (r.result === 'win' || r.result === 'loss' || r.result === 'draw') ? r.result : 'win',
          created_at: r.created_at
        };
      })
      .sort((a: UserTournamentReportRound, b: UserTournamentReportRound) => a.round_number - b.round_number);
  } else if (Array.isArray(deckData?.rounds) && deckData.rounds.length > 0) {
    rounds = deckData.rounds.map((r: any, idx: number) => {
      const rawDeckName = (r.opponent_deck || r.opponent_deck_name || '').toLowerCase().trim();
      const fallbackMeta = r.opponent_meta_deck_id
        ? DEFAULT_META_DECKS.find(d => String(d.id) === String(r.opponent_meta_deck_id))
        : (rawDeckName
          ? DEFAULT_META_DECKS.find(d =>
              d.name.toLowerCase().trim() === rawDeckName ||
              (d.display_name && d.display_name.toLowerCase().trim() === rawDeckName) ||
              (d.slug && d.slug.toLowerCase().trim() === rawDeckName) ||
              rawDeckName.includes(d.name.toLowerCase().trim())
            )
          : null);

      return {
        ...r,
        round_number: Number(r.round_number || (idx + 1)),
        opponent_deck: r.opponent_deck || r.opponent_deck_name || fallbackMeta?.display_name || fallbackMeta?.name || null,
        opponent_meta_deck_id: r.opponent_meta_deck_id || (fallbackMeta?.id ? Number(fallbackMeta.id) : null),
        opponent_meta_deck: r.opponent_meta_deck || fallbackMeta || null,
        opponent_deck_image_url: r.opponent_deck_image_url || fallbackMeta?.image_url || null,
        result: (r.result === 'win' || r.result === 'loss' || r.result === 'draw') ? r.result : 'win'
      };
    });
  }

  const formatName = item.game_formats?.name || item.tournament_formats?.name || TOURNAMENT_FORMATS.find(f => String(f.id) === String(item.format_id))?.name || null;
  const typeName = item.tournament_types?.name || TOURNAMENT_TYPES.find(t => String(t.id) === String(item.tournament_type_id))?.name || null;
  const catName = item.tournament_event_categories?.name || EVENT_CATEGORIES.find(c => String(c.id) === String(item.event_category_id))?.name || null;
  const setName = item.game_sets?.name ? `${item.game_sets.name}${item.game_sets.code ? ` (${item.game_sets.code})` : ''}` : (item.game_sets?.code || null);

  const rawGameName = item.games?.name || (Array.isArray(item.games) ? item.games[0]?.name : null);
  const fallbackGameName = item.game_id ? getGameNameById(item.game_id) : null;
  const resolvedGameName = rawGameName || (item.game_name && item.game_name !== 'Cardgame' && item.game_name !== 'TCG' && isNaN(Number(item.game_name)) ? item.game_name : (fallbackGameName || 'Cardgame'));

  return {
    id: String(item.id),
    user_id: item.user_id,
    user_name: item.users?.fullname || item.users?.username || item.user_name || 'Jogador',
    user_avatar: item.users?.avatar || item.user_avatar,
    game_id: item.game_id,
    game_name: resolvedGameName,
    name: item.name,
    played_at: item.played_at ? String(item.played_at).substring(0, 10) : new Date().toISOString().substring(0, 10),

    deck_id: item.deck_id || null,
    deck_name: item.deck_name || item.user_decks?.name || 'Deck Desconhecido',
    deck_data: deckData,
    deck_leader: deckData?.leader || deckData?.leader_name || item.deck_leader || null,
    deck_leader_image: deckData?.leader_image || deckData?.leader_image_url || item.deck_leader_image || null,

    format_id: item.format_id || null,
    format_name: formatName,
    tournament_type_id: item.tournament_type_id || null,
    tournament_type_name: typeName,
    event_category_id: item.event_category_id || null,
    event_category_name: catName,
    tourney_set_id: item.tourney_set_id || null,
    tourney_set_name: setName,

    placement: item.placement !== null && item.placement !== undefined ? Number(item.placement) : null,
    total_players: item.total_players !== null && item.total_players !== undefined ? Number(item.total_players) : null,

    wins: Number(item.wins || 0),
    losses: Number(item.losses || 0),
    draws: Number(item.draws || 0),

    official_tournament_id: item.official_tournament_id || null,
    validation_status: item.validation_status || 'self_reported',

    created_at: item.created_at || new Date().toISOString(),
    updated_at: item.updated_at || item.created_at,

    store_name: deckData?.store_name || item.store_name || null,
    location: deckData?.location || item.location || null,
    notes: deckData?.notes || item.notes || null,
    rounds
  };
};

/**
 * Busca reports de torneios do banco de dados (public.user_tournament_reports) com fallback local
 */
export const fetchUserTournamentReports = async (): Promise<UserTournamentReport[]> => {
  try {
    const { data, error } = await supabase
      .from('user_tournament_reports')
      .select(`
        *,
        games(id, name),
        users:user_id(id, username, fullname, avatar),
        user_decks:deck_id(id, name),
        game_formats:format_id(id, name),
        tournament_event_categories:event_category_id(id, name),
        tournament_types:tournament_type_id(id, name),
        game_sets:tourney_set_id(id, name, code),
        user_tournament_report_rounds(
          id,
          report_id,
          round_number,
          opponent_name,
          opponent_deck,
          won_dice,
          play_order,
          result,
          created_at,
          opponent_meta_deck_id
        )
      `)
      .order('played_at', { ascending: false });

    if (!error && Array.isArray(data) && data.length > 0) {
      const mapped = data.map(mapDbReportToUi);
      saveStoredReports(mapped);
      return mapped;
    }
  } catch (err) {
    console.warn('Could not fetch from Supabase user_tournament_reports with full relations, trying simple query:', err);
  }

  // Fallback com queries separadas para reports e rodadas
  try {
    const { data: simpleData, error: simpleErr } = await supabase
      .from('user_tournament_reports')
      .select(`
        *,
        games(id, name),
        users:user_id(id, username, fullname, avatar),
        user_decks:deck_id(id, name),
        game_formats:format_id(id, name),
        tournament_event_categories:event_category_id(id, name),
        tournament_types:tournament_type_id(id, name),
        game_sets:tourney_set_id(id, name, code)
      `)
      .order('played_at', { ascending: false });

    if (!simpleErr && Array.isArray(simpleData) && simpleData.length > 0) {
      let allRounds: any[] = [];
      try {
        const { data: roundsData } = await supabase
          .from('user_tournament_report_rounds')
          .select('id, report_id, round_number, opponent_name, opponent_deck, won_dice, play_order, result, created_at, opponent_meta_deck_id')
          .order('round_number', { ascending: true });
        if (Array.isArray(roundsData)) {
          allRounds = roundsData;
        }
      } catch (rErr) {
        console.warn('Separate rounds fetch failed:', rErr);
      }

      const combined = simpleData.map(rep => ({
        ...rep,
        user_tournament_report_rounds: allRounds.filter(r => String(r.report_id) === String(rep.id))
      }));

      const mapped = combined.map(mapDbReportToUi);
      saveStoredReports(mapped);
      return mapped;
    }
  } catch (fallbackErr) {
    console.warn('Simple query fallback also failed:', fallbackErr);
  }

  return getStoredReports();
};

/**
 * Salva um report de torneio diretamente na tabela public.user_tournament_reports
 * e persiste as rodadas na tabela public.user_tournament_report_rounds
 */
export const saveUserTournamentReport = async (
  reportData: {
    name: string;
    game_id: number | string;
    played_at: string;
    wins?: number;
    losses?: number;
    draws?: number;
    placement?: number | string | null;
    total_players?: number | null;
    deck_id?: string | null;
    deck_name?: string | null;
    deck_data?: any;
    format_id?: number | string | null;
    tournament_type_id?: number | string | null;
    event_category_id?: number | string | null;
    tourney_set_id?: number | string | null;
    store_name?: string | null;
    location?: string | null;
    notes?: string | null;
    rounds?: UserTournamentReportRound[];
  },
  reportId?: string | number
): Promise<UserTournamentReport> => {
  // 1. Obter usuário autenticado
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error('Você precisa estar autenticado para criar ou editar reports de torneios.');
  }

  const currentUser = await getUserByAuthId(session.user.id);
  if (!currentUser?.id) {
    throw new Error('Perfil de usuário não encontrado. Por favor, faça login novamente.');
  }

  const dbUserId = Number(currentUser.id);
  const isAdmin = currentUser.role === 'admin';

  // 2. Se for edição, verificar se o report pertence ao usuário logado
  if (reportId && !String(reportId).startsWith('rep-')) {
    const numericReportId = Number(reportId);
    const { data: existingReport, error: fetchErr } = await supabase
      .from('user_tournament_reports')
      .select('id, user_id')
      .eq('id', numericReportId)
      .maybeSingle();

    if (fetchErr) {
      console.warn('Error verifying report ownership before save:', fetchErr);
    }

    if (existingReport && Number(existingReport.user_id) !== dbUserId && !isAdmin) {
      throw new Error('Acesso negado: Você só pode editar os seus próprios reports.');
    }
  }

  // Se houver rodadas, sincronizar o total de vitórias/derrotas/empates automaticamente se necessário
  let calcWins = Number(reportData.wins || 0);
  let calcLosses = Number(reportData.losses || 0);
  let calcDraws = Number(reportData.draws || 0);

  if (Array.isArray(reportData.rounds) && reportData.rounds.length > 0) {
    let w = 0, l = 0, d = 0;
    reportData.rounds.forEach(r => {
      if (r.result === 'win') w++;
      else if (r.result === 'loss') l++;
      else if (r.result === 'draw') d++;
    });
    // Se o usuário adicionou rodadas, o record reflete as rodadas
    calcWins = w;
    calcLosses = l;
    calcDraws = d;
  }

  // Organizar snapshot e dados extras em deck_data (jsonb)
  const combinedDeckData = {
    ...(reportData.deck_data || {}),
    store_name: reportData.store_name || null,
    location: reportData.location || null,
    notes: reportData.notes || null,
    rounds: reportData.rounds || []
  };

  // Tratar colocação como número inteiro (ou null)
  let numericPlacement: number | null = null;
  if (reportData.placement !== undefined && reportData.placement !== null && reportData.placement !== '') {
    const parsed = parseInt(String(reportData.placement).replace(/\D/g, ''), 10);
    if (!isNaN(parsed) && parsed > 0) {
      numericPlacement = parsed;
    }
  }

  // Payload formatado rigorosamente para a tabela public.user_tournament_reports
  const dbPayload: any = {
    user_id: dbUserId,
    game_id: Number(reportData.game_id),
    name: reportData.name.trim(),
    played_at: reportData.played_at,
    wins: calcWins,
    losses: calcLosses,
    draws: calcDraws,
    deck_id: reportData.deck_id && reportData.deck_id !== 'manual' ? reportData.deck_id : null,
    deck_name: reportData.deck_name ? reportData.deck_name.trim() : null,
    deck_data: combinedDeckData,
    placement: numericPlacement,
    total_players: reportData.total_players ? Number(reportData.total_players) : null,
    format_id: reportData.format_id ? Number(reportData.format_id) : null,
    tournament_type_id: reportData.tournament_type_id ? Number(reportData.tournament_type_id) : null,
    event_category_id: reportData.event_category_id ? Number(reportData.event_category_id) : null,
    tourney_set_id: reportData.tourney_set_id ? Number(reportData.tourney_set_id) : null,
    validation_status: 'self_reported'
  };

  let savedRecord: any = null;
  let savedNumericId: number | null = null;

  // 2. Tentar salvar no Supabase
  try {
    if (reportId && !String(reportId).startsWith('rep-')) {
      savedNumericId = Number(reportId);
      
      let updateQuery = supabase
        .from('user_tournament_reports')
        .update({
          ...dbPayload,
          updated_at: new Date().toISOString()
        })
        .eq('id', savedNumericId);

      // Usuário comum só pode atualizar seu próprio registro
      if (!isAdmin) {
        updateQuery = updateQuery.eq('user_id', dbUserId);
      }

      const { data, error } = await updateQuery
        .select(`
          *,
          games(id, name),
          users:user_id(id, username, fullname, avatar),
          user_decks:deck_id(id, name),
          game_formats:format_id(id, name),
          tournament_event_categories:event_category_id(id, name),
          tournament_types:tournament_type_id(id, name),
          game_sets:tourney_set_id(id, name, code)
        `)
        .maybeSingle();

      if (error) {
        throw error;
      }
      if (data) {
        savedRecord = mapDbReportToUi(data);
      }
    } else {
      const { data, error } = await supabase
        .from('user_tournament_reports')
        .insert(dbPayload)
        .select(`
          *,
          games(id, name),
          users:user_id(id, username, fullname, avatar),
          user_decks:deck_id(id, name),
          game_formats:format_id(id, name),
          tournament_event_categories:event_category_id(id, name),
          tournament_types:tournament_type_id(id, name),
          game_sets:tourney_set_id(id, name, code)
        `)
        .maybeSingle();

      if (error) {
        throw error;
      }
      if (data) {
        savedRecord = mapDbReportToUi(data);
        savedNumericId = Number(data.id);
      }
    }
  } catch (err: any) {
    console.error('Error persisting to Supabase user_tournament_reports:', err);
    throw new Error(err.message || 'Falha ao salvar report de torneio no banco de dados.');
  }

  // 3. Salvar rodadas na tabela public.user_tournament_report_rounds se o report foi persistido no banco
  if (savedNumericId && Array.isArray(reportData.rounds)) {
    try {
      // 3.1. Normalizar rodadas garantindo round_number estritamente único e sequencial (1, 2, 3...)
      const roundsToUpsert = reportData.rounds.map((r, index) => {
        const roundNum = index + 1;
        const validResult: 'win' | 'loss' | 'draw' =
          (r.result === 'win' || r.result === 'loss' || r.result === 'draw') ? r.result : 'win';
        const validPlayOrder: 1 | 2 | null =
          r.play_order === 1 || r.play_order === 2 ? r.play_order : null;

        return {
          report_id: savedNumericId!,
          round_number: roundNum,
          opponent_name: r.opponent_name?.trim() || null,
          opponent_deck: (r.opponent_deck || (r as any).opponent_deck_name)?.trim() || null,
          opponent_meta_deck_id: r.opponent_meta_deck_id ? Number(r.opponent_meta_deck_id) : null,
          won_dice: r.won_dice !== undefined && r.won_dice !== null ? Boolean(r.won_dice) : null,
          play_order: validPlayOrder,
          result: validResult
        };
      });

      // 3.2. Deletar rodadas excedentes (caso o usuário tenha diminuído a quantidade de rodadas)
      try {
        if (roundsToUpsert.length === 0) {
          await supabase
            .from('user_tournament_report_rounds')
            .delete()
            .eq('report_id', savedNumericId);
        } else {
          await supabase
            .from('user_tournament_report_rounds')
            .delete()
            .eq('report_id', savedNumericId)
            .gt('round_number', roundsToUpsert.length);
        }
      } catch (trimErr) {
        console.warn('Could not trim excess rounds:', trimErr);
      }

      if (roundsToUpsert.length > 0) {
        let insertedRoundsData: any[] | null = null;

        // Tentar upsert com onConflict no par único da tabela (report_id, round_number)
        const { data: upsertedRounds, error: roundsErr } = await supabase
          .from('user_tournament_report_rounds')
          .upsert(roundsToUpsert, { onConflict: 'report_id,round_number' })
          .select('*');

        if (roundsErr) {
          console.warn('First upsert attempt on user_tournament_report_rounds failed, retrying with opponent_meta_deck_id as null:', roundsErr);
          // Se falhou (por exemplo devido a FK constraint de opponent_meta_deck_id inexistente), tenta sem o meta_deck_id
          const fallbackRounds = roundsToUpsert.map(r => ({
            ...r,
            opponent_meta_deck_id: null
          }));
          const { data: retryRounds, error: retryErr } = await supabase
            .from('user_tournament_report_rounds')
            .upsert(fallbackRounds, { onConflict: 'report_id,round_number' })
            .select('*');

          if (!retryErr && Array.isArray(retryRounds)) {
            insertedRoundsData = retryRounds;
          } else {
            console.warn('Fallback upsert failed, attempting explicit clean and insert:', retryErr);
            // Último fallback de contingência: limpa rodadas anteriores e faz insert simples
            try {
              await supabase
                .from('user_tournament_report_rounds')
                .delete()
                .eq('report_id', savedNumericId);

              const { data: finalInsert, error: finalErr } = await supabase
                .from('user_tournament_report_rounds')
                .insert(fallbackRounds)
                .select('*');

              if (!finalErr && Array.isArray(finalInsert)) {
                insertedRoundsData = finalInsert;
              } else {
                console.error('Final round insert attempt failed:', finalErr);
              }
            } catch (cleanErr) {
              console.error('Exception cleaning rounds:', cleanErr);
            }
          }
        } else if (Array.isArray(upsertedRounds)) {
          insertedRoundsData = upsertedRounds;
        }

        if (insertedRoundsData && savedRecord) {
          savedRecord.rounds = insertedRoundsData
            .map(r => {
              const fallbackMeta = r.opponent_meta_deck_id
                ? DEFAULT_META_DECKS.find(d => String(d.id) === String(r.opponent_meta_deck_id))
                : null;
              return {
                id: r.id,
                report_id: r.report_id,
                round_number: Number(r.round_number),
                opponent_name: r.opponent_name || null,
                opponent_deck: r.opponent_deck || fallbackMeta?.display_name || fallbackMeta?.name || null,
                opponent_meta_deck_id: r.opponent_meta_deck_id ? Number(r.opponent_meta_deck_id) : null,
                opponent_meta_deck: fallbackMeta || null,
                opponent_deck_image_url: fallbackMeta?.image_url || null,
                won_dice: r.won_dice !== null && r.won_dice !== undefined ? Boolean(r.won_dice) : null,
                play_order: r.play_order === 1 || r.play_order === 2 ? r.play_order : null,
                result: (r.result === 'win' || r.result === 'loss' || r.result === 'draw') ? r.result : 'win',
                created_at: r.created_at
              };
            })
            .sort((a, b) => a.round_number - b.round_number);
        } else if (savedRecord) {
          savedRecord.rounds = roundsToUpsert;
        }
      } else if (savedRecord) {
        savedRecord.rounds = [];
      }
    } catch (roundErr) {
      console.warn('Exception managing user_tournament_report_rounds:', roundErr);
    }
  }

  // 4. Fallback local para garantir persistência imediata
  if (!savedRecord) {
    const generatedId = reportId ? String(reportId) : `rep-${Date.now()}`;
    savedRecord = {
      id: generatedId,
      user_id: dbUserId,
      user_name: currentUser?.fullname || currentUser?.username || 'Você',
      user_avatar: currentUser?.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=150',
      game_id: Number(reportData.game_id),
      game_name: (reportData.game_id ? getGameNameById(reportData.game_id) : null) || 'Cardgame',
      name: reportData.name.trim(),
      played_at: reportData.played_at,
      deck_id: dbPayload.deck_id,
      deck_name: dbPayload.deck_name || 'Deck Desconhecido',
      deck_data: combinedDeckData,
      deck_leader: combinedDeckData.leader || null,
      deck_leader_image: combinedDeckData.leader_image || null,
      format_id: dbPayload.format_id,
      format_name: TOURNAMENT_FORMATS.find(f => String(f.id) === String(dbPayload.format_id))?.name || null,
      tournament_type_id: dbPayload.tournament_type_id,
      tournament_type_name: TOURNAMENT_TYPES.find(t => String(t.id) === String(dbPayload.tournament_type_id))?.name || null,
      event_category_id: dbPayload.event_category_id,
      event_category_name: EVENT_CATEGORIES.find(c => String(c.id) === String(dbPayload.event_category_id))?.name || null,
      tourney_set_id: dbPayload.tourney_set_id,
      tourney_set_name: null,
      placement: numericPlacement,
      total_players: dbPayload.total_players,
      wins: calcWins,
      losses: calcLosses,
      draws: calcDraws,
      store_name: reportData.store_name || null,
      location: reportData.location || null,
      notes: reportData.notes || null,
      rounds: reportData.rounds || [],
      validation_status: 'self_reported',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  } else if (!savedRecord.rounds || savedRecord.rounds.length === 0) {
    savedRecord.rounds = reportData.rounds || [];
  }

  // Sincronizar cache local
  const currentList = getStoredReports();
  const existingIdx = currentList.findIndex(r => String(r.id) === String(savedRecord.id));
  if (existingIdx >= 0) {
    currentList[existingIdx] = savedRecord;
  } else {
    currentList.unshift(savedRecord);
  }
  saveStoredReports(currentList);

  return savedRecord;
};

/**
 * Deleta um report do banco de dados e do cache local
 */
export const deleteUserTournamentReport = async (reportId: string | number): Promise<boolean> => {
  // 1. Obter usuário autenticado
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error('Você precisa estar conectado para excluir um report.');
  }

  const currentUser = await getUserByAuthId(session.user.id);
  if (!currentUser?.id) {
    throw new Error('Perfil de usuário não encontrado.');
  }

  const dbUserId = Number(currentUser.id);
  const isAdmin = currentUser.role === 'admin';

  try {
    if (!String(reportId).startsWith('rep-')) {
      const numericReportId = Number(reportId);

      // Verificar permissão antes de deletar
      const { data: existingReport, error: checkErr } = await supabase
        .from('user_tournament_reports')
        .select('id, user_id')
        .eq('id', numericReportId)
        .maybeSingle();

      if (checkErr) {
        console.warn('Error checking report before delete:', checkErr);
      }

      if (existingReport && Number(existingReport.user_id) !== dbUserId && !isAdmin) {
        throw new Error('Acesso negado: Você só pode excluir os seus próprios reports.');
      }

      // Deletar rodadas vinculadas
      await supabase
        .from('user_tournament_report_rounds')
        .delete()
        .eq('report_id', numericReportId);

      let deleteQuery = supabase
        .from('user_tournament_reports')
        .delete()
        .eq('id', numericReportId);

      if (!isAdmin) {
        deleteQuery = deleteQuery.eq('user_id', dbUserId);
      }

      const { error: delError } = await deleteQuery;
      if (delError) {
        throw delError;
      }
    }
  } catch (err: any) {
    console.error('Error deleting from supabase user_tournament_reports:', err);
    throw new Error(err.message || 'Falha ao excluir report de torneio do banco de dados.');
  }

  const currentList = getStoredReports().filter(r => String(r.id) !== String(reportId));
  saveStoredReports(currentList);
  return true;
};
